import Tour from 'antd/es/tour/index'
import { TourProps } from 'antd/lib'
import { InfoCircleFilled } from '@ant-design/icons'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'
import { useTranslation } from 'react-i18next'
import { selectUser } from '../../../features/user/user.slice.ts'
import {
  canCreateIdeaPermissions,
  hasPermissionAndFirmCheck,
} from '../../../utils/permissions.ts'
import { useTutorial } from '../../../contexts/use-tutorial.ts'

interface IdeaListTourProps {
  openTour: boolean
  setOpenTour: (open: boolean) => void
}

export const IdeaListTour: React.FC<IdeaListTourProps> = ({
  openTour,
  setOpenTour,
}) => {
  const { refs } = useTutorial()
  const themeStyle = useAppSelector(selectThemeStyle)
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUser)

  const canCreateIdea = hasPermissionAndFirmCheck(
    canCreateIdeaPermissions,
    userInfo.firmId,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const previousButtonProps = {
    children: t('tutorial.previous'),
    style: {
      backgroundColor: themeStyle.colorPrimaryBg,
      color: themeStyle.colorText,
      borderColor: 'transparent',
    },
  }

  const nextButtonProps = {
    children: t('tutorial.next'),
    style: {
      backgroundColor: themeStyle.colorPrimaryBg,
      color: themeStyle.colorText,
    },
  }

  const allSteps: TourProps['steps'] = [
    canCreateIdea && {
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InfoCircleFilled
            style={{ fontSize: '14px', color: themeStyle.gold6 }}
          />
          {t('tutorial.idea_list.create_idea')}
        </span>
      ),
      description: t('tutorial.idea_list.create_idea_description'),
      target: () => refs.createIdea?.current as HTMLElement,
      placement: 'top',
      nextButtonProps: nextButtonProps,
    },
    {
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InfoCircleFilled
            style={{ fontSize: '14px', color: themeStyle.gold6 }}
          />
          {t('tutorial.idea_list.summary_data')}
        </span>
      ),
      description: canCreateIdea
        ? t('tutorial.idea_list.summary_data_description')
        : t('tutorial.idea_list.summary_data_description_non_contributor'),
      target: () => refs.summaryData?.current as HTMLElement,
      placement: 'top',
      nextButtonProps: nextButtonProps,
      prevButtonProps: previousButtonProps,
    },
    {
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InfoCircleFilled
            style={{ fontSize: '14px', color: themeStyle.gold6 }}
          />
          {t('tutorial.idea_list.table_view')}
        </span>
      ),
      description: t('tutorial.idea_list.table_view_description'),
      target: () => refs.tableColumn?.current as HTMLElement,
      placement: 'top',
      prevButtonProps: previousButtonProps,
      nextButtonProps: {
        children: t('tutorial.finish'),
        style: {
          backgroundColor: themeStyle.colorPrimaryBg,
          color: themeStyle.colorText,
        },
      },
    },
  ].filter(Boolean) as TourProps['steps']

  const allRefsReady =
    (!canCreateIdea || refs.createIdea?.current) && refs.summaryData?.current

  return allRefsReady ? (
    <Tour steps={allSteps} open={openTour} onClose={() => setOpenTour(false)} />
  ) : null
}
